import React from 'react';
import { User } from './utils/types';
import { formatDate } from './utils/helpers';
import { canCreateUsers } from './utils/permissions';

interface UsersTableProps {
  users: User[];
  currentUserRole: string;
  onToggleUserStatus: (userId: string) => void;
  onEditUser: (user: User) => void;
  onDeleteUser: (user: User) => void;
  onCreateUser: () => void;
}

const UsersTable: React.FC<UsersTableProps> = ({
  users,
  currentUserRole,
  onToggleUserStatus,
  onEditUser,
  onDeleteUser,
  onCreateUser
}) => {
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        {canCreateUsers(currentUserRole) && (
          <button
            onClick={onCreateUser}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 hover:shadow-lg transition-all duration-300 flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Créer un utilisateur
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl border-2 sm:border-4 border-green-100 overflow-hidden">
        <div className="px-4 sm:px-6 py-4 sm:py-5 border-b-2 sm:border-b-4 border-blue-200 bg-gradient-to-r from-blue-600 to-cyan-600">
          <h3 className="text-lg sm:text-xl font-bold text-white flex items-center">
            <svg className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
            Utilisateurs ({users.length})
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utilisateur</th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">E-mail</th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rôle</th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Date création</th>
                <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <UserRow
                  key={user._id}
                  user={user}
                  currentUserRole={currentUserRole}
                  onToggleStatus={onToggleUserStatus}
                  onEdit={onEditUser}
                  onDelete={onDeleteUser}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

interface UserRowProps {
  user: User;
  currentUserRole: string;
  onToggleStatus: (userId: string) => void;
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
}

const UserRow: React.FC<UserRowProps> = ({ user, currentUserRole, onToggleStatus, onEdit, onDelete }) => {
  const getRoleInfo = (role: string) => {
    switch (role) {
      case 'admin_bank': return { text: 'Admin Banque', classes: 'bg-purple-100 text-purple-800' };
      case 'agent_saisie': return { text: 'Agent Saisie', classes: 'bg-green-100 text-green-800' };
      case 'auditeur': return { text: 'Auditeur', classes: 'bg-orange-100 text-orange-800' };
      case 'conformité': return { text: 'Conformité', classes: 'bg-pink-100 text-pink-800' };
      case 'super_admin': return { text: 'Super Admin', classes: 'bg-red-100 text-red-800' };
      default: return { text: 'Superviseur', classes: 'bg-blue-100 text-blue-800' };
    }
  };

  const roleInfo = getRoleInfo(user.role);

  return (
    <tr className="hover:bg-gradient-to-r hover:from-green-50 hover:to-white transition-all duration-300">
      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center mr-2 sm:mr-3">
            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900 truncate max-w-[100px] sm:max-w-none">{user.username}</p>
          </div>
        </div>
      </td>
      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
        <span className="text-sm text-gray-900 truncate max-w-[120px] sm:max-w-none block">{user.email}</span>
      </td>
      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${roleInfo.classes}`}>
          {roleInfo.text}
        </span>
      </td>
      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
        <button
          onClick={() => onToggleStatus(user._id)}
          disabled={currentUserRole !== 'admin_bank' && currentUserRole !== 'super_admin'}
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
            user.isActive
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          } ${(currentUserRole !== 'admin_bank' && currentUserRole !== 'super_admin') ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          {user.isActive ? 'Actif' : 'Inactif'}
        </button>
      </td>
      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
        {formatDate(user.createdAt)}
      </td>
      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex justify-end space-x-1 sm:space-x-2 flex-wrap gap-1">
          {(currentUserRole === 'admin_bank' || currentUserRole === 'super_admin') && (
            <>
              <button
                onClick={() => onEdit(user)}
                className="text-green-600 hover:text-green-800 bg-green-50 hover:bg-green-100 px-2 sm:px-3 py-1 rounded-lg transition-all duration-300 text-xs sm:text-sm"
              >
                Modifier
              </button>
              <button
                onClick={() => onDelete(user)}
                className="text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-2 sm:px-3 py-1 rounded-lg transition-all duration-300 text-xs sm:text-sm"
              >
                Supprimer
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

export default UsersTable;
